import { Controller } from "@hotwired/stimulus";
import SlimSelect from "slim-select";

export default class ProductSelectController extends Controller {
  static targets = [
    "productSelect",
    "unitValue",
    "suggestedValue",
    "quantity",
    "totalValue",
  ];

  connect() {
    const elementOptions = JSON.parse(
      this.productSelectTarget.dataset?.slimOptions || "{}",
    );

    const selectedOptionFromDom = this.getSelectedOptionFromDom();
    this.selectedProductOption =
      (selectedOptionFromDom && selectedOptionFromDom.value !== ""
        ? selectedOptionFromDom
        : null) || this.getSelectedOptionFromDataset();
    this.productUnitValueById = new Map();

    if (this.selectedProductOption?.value) {
      this.productUnitValueById.set(
        String(this.selectedProductOption.value),
        this.parseNumber(this.selectedProductOption.unit_value),
      );
    }

    this.productSelect = new SlimSelect({
      select: this.productSelectTarget,
      settings: {
        placeholderText: "Escolha...",
        searchText: "Nenhum resultado...",
        searchPlaceholder: "Pesquise...",
        allowDeselect: true,
      },
      events: {
        search: (search) => this.searchProducts(search),
        afterChange: (products) => {
          const product = Array.isArray(products) ? products[0] : null;
          this.selectedProductOption = this.normalizeSelectedOption(product);

          if (this.hasUnitValueTarget) {
            this.unitValueTarget.value = this.formatDecimal(
              this.resolveSelectedUnitValue(product),
            );
          }

          this.updateTotalValue();
        },
      },
      ...elementOptions,
    });

    this.importProductHandler = (event) => {
      this.applyImportedProduct(event.detail || {});
    };

    this.element.addEventListener(
      "cart-import:set-product",
      this.importProductHandler,
    );

    this.preloadInitialProducts();
    this.updateTotalValue();
  }

  disconnect() {
    if (this.importProductHandler) {
      this.element.removeEventListener(
        "cart-import:set-product",
        this.importProductHandler,
      );
      this.importProductHandler = null;
    }

    if (this.productSelectTimeout) {
      clearTimeout(this.productSelectTimeout);
      this.productSelectTimeout = null;
    }

    if (this.productSelect) {
      this.productSelect.destroy();
      this.productSelect = null;
    }
  }

  applyImportedProduct({ productId, productLabel, unitValue }) {
    if (!productId) return;

    const selectedValue = String(productId);
    const selectedLabel =
      productLabel && String(productLabel).trim().length > 0
        ? String(productLabel)
        : `Produto ${selectedValue}`;
    const formattedUnitValue = this.formatDecimal(unitValue || 0);

    this.upsertNativeOption(selectedValue, selectedLabel, formattedUnitValue);

    if (
      this.productSelect &&
      typeof this.productSelect.getData === "function" &&
      typeof this.productSelect.setData === "function"
    ) {
      const currentData = this.productSelect.getData() || [];
      const normalizedData = currentData.filter(
        (option) => String(option.value) !== selectedValue,
      );

      this.productSelect.setData([
        {
          text: selectedLabel,
          value: selectedValue,
          selected: true,
          data: { unit_value: formattedUnitValue },
        },
        ...normalizedData.map((option) => ({ ...option, selected: false })),
      ]);
    }

    if (
      this.productSelect &&
      typeof this.productSelect.setSelected === "function"
    ) {
      this.productSelect.setSelected(selectedValue);
    }

    this.productSelectTarget.value = selectedValue;

    this.selectedProductOption = {
      value: selectedValue,
      text: selectedLabel,
      unit_value: formattedUnitValue,
      data: {
        unit_value: formattedUnitValue,
      },
    };

    this.productUnitValueById.set(selectedValue, this.parseNumber(unitValue));

    if (this.hasUnitValueTarget) {
      this.unitValueTarget.value = formattedUnitValue;
    }

    this.updateTotalValue();
  }

  upsertNativeOption(value, text, unitValue) {
    const nativeOptions = Array.from(this.productSelectTarget.options || []);
    const existingOption = nativeOptions.find(
      (option) => String(option.value) === String(value),
    );

    if (existingOption) {
      existingOption.text = text;
      existingOption.dataset.unitValue = unitValue;
      existingOption.selected = true;
      return;
    }

    const option = new Option(text, String(value), true, true);
    option.dataset.unitValue = unitValue;
    this.productSelectTarget.add(option);
  }

  updateTotalValue() {
    if (!this.hasTotalValueTarget) return;

    const quantity = this.hasQuantityTarget
      ? this.parseNumber(this.quantityTarget.value)
      : 0;
    const suggestedValue = this.hasSuggestedValueTarget
      ? this.parseNumber(this.suggestedValueTarget.value)
      : 0;
    const unitValue = this.hasUnitValueTarget
      ? this.parseNumber(this.unitValueTarget.value)
      : 0;

    const baseValue = suggestedValue > 0 ? suggestedValue : unitValue;
    const totalValue = quantity > 0 ? baseValue * quantity : 0;

    this.totalValueTarget.value = this.formatDecimal(totalValue);

    this.element.dispatchEvent(
      new CustomEvent("order-product:total-changed", { bubbles: true }),
    );
  }

  parseNumber(value) {
    if (value === null || value === undefined || value === "") return 0;

    const sanitizedValue = String(value).replace(",", ".");
    const parsedValue = Number.parseFloat(sanitizedValue);

    return Number.isNaN(parsedValue) ? 0 : parsedValue;
  }

  formatDecimal(value) {
    return this.parseNumber(value).toFixed(2);
  }

  async searchProducts(search) {
    if (this.productSelectTimeout) clearTimeout(this.productSelectTimeout);

    return await new Promise((resolve) => {
      this.productSelectTimeout = setTimeout(async () => {
        try {
          const data = await this.fetchProducts(search);
          const normalizedData = this.normalizeProductOptions(data);

          resolve(this.mergeSelectedOption(normalizedData));
        } catch (_error) {
          resolve([]);
        }
      }, 300);
    });
  }

  async preloadInitialProducts() {
    if (
      !this.productSelect ||
      typeof this.productSelect.setData !== "function" ||
      typeof this.productSelect.setSelected !== "function"
    ) {
      return;
    }

    try {
      const data = await this.fetchProducts("");
      const normalizedData = this.normalizeProductOptions(data);
      const mergedData = this.mergeSelectedOption(normalizedData);

      this.productSelect.setData(mergedData);

      const selectedOption = this.getSelectedOption();

      if (selectedOption?.value) {
        this.productSelect.setSelected(String(selectedOption.value));

        if (this.hasUnitValueTarget) {
          this.unitValueTarget.value = this.formatDecimal(
            this.resolveSelectedUnitValue({
              value: selectedOption.value,
              data: { unit_value: selectedOption.unit_value },
            }),
          );
        }

        this.updateTotalValue();
      }
    } catch (_error) {}
  }

  async fetchProducts(search) {
    const urlString = "/admin/products/slim_select_search";
    const url = new URL(urlString, document.baseURI);
    url.searchParams.append("search", search || "");

    const response = await fetch(url);
    return await response.json();
  }

  normalizeProductOptions(data) {
    if (!Array.isArray(data)) return data;

    const normalizedData = data.map((option) => {
      if (!option || typeof option !== "object") return option;

      const optionId =
        option.product_id ?? option.id ?? option.value ?? option.unit_value;
      const optionUnitValue =
        option.unit_value ?? option.price ?? option.value ?? 0;

      return {
        ...option,
        value: optionId,
        data: {
          ...(option.data || {}),
          unit_value: option.data?.unit_value ?? optionUnitValue,
        },
      };
    });

    normalizedData.forEach((option) => this.saveUnitValueForOption(option));

    return normalizedData;
  }

  mergeSelectedOption(data) {
    if (!Array.isArray(data)) return data;

    const selectedOption = this.getSelectedOption();

    if (!selectedOption || selectedOption.value === "") {
      return data;
    }

    const selectedValue = String(selectedOption.value);
    const selectedText = selectedOption.text || "";

    const hasSelectedOption = data.some(
      (option) => String(option.value) === selectedValue,
    );

    if (hasSelectedOption) {
      return data.map((option) => {
        if (String(option.value) !== selectedValue) return option;

        return {
          ...option,
          text: option.text || selectedText,
          unit_value: option.unit_value ?? selectedOption.unit_value,
          data: {
            ...(option.data || {}),
            unit_value:
              option.data?.unit_value ??
              selectedOption.data?.unit_value ??
              selectedOption.unit_value,
          },
          selected: true,
        };
      });
    }

    const mergedData = [{ ...selectedOption, selected: true }, ...data];
    mergedData.forEach((option) => this.saveUnitValueForOption(option));

    return mergedData;
  }

  getSelectedOption() {
    const domOption = this.getSelectedOptionFromDom();

    if (domOption && domOption.value !== "") {
      return domOption;
    }

    return this.selectedProductOption;
  }

  getSelectedOptionFromDom() {
    const selectedOption = this.productSelectTarget.selectedOptions?.[0];

    if (!selectedOption) return null;
    if (selectedOption.value === "") return null;

    return {
      value: selectedOption.value,
      text: selectedOption.text,
      unit_value: selectedOption.dataset?.unitValue,
      data: {
        unit_value: selectedOption.dataset?.unitValue,
      },
    };
  }

  getSelectedOptionFromDataset() {
    const selectedId = this.productSelectTarget.dataset?.selectedProductId;
    if (!selectedId) return null;

    const selectedName = this.productSelectTarget.dataset?.selectedProductName;
    const selectedUnitValue =
      this.productSelectTarget.dataset?.selectedProductUnitValue;

    return {
      value: String(selectedId),
      text: selectedName || `Produto ${selectedId}`,
      unit_value: selectedUnitValue,
      data: {
        unit_value: selectedUnitValue,
      },
    };
  }

  normalizeSelectedOption(product) {
    if (!product || product.value === undefined || product.value === null) {
      return null;
    }

    return {
      value: product.value,
      text: product.text || "",
      unit_value: this.resolveProductUnitValue(product),
      data: {
        ...(product.data || {}),
        unit_value: this.resolveProductUnitValue(product),
      },
    };
  }

  resolveProductUnitValue(product) {
    if (!product) return 0;

    return product.data?.unit_value ?? product.unit_value ?? 0;
  }

  resolveSelectedUnitValue(product) {
    const productId = this.resolveSelectedProductId(product);

    if (!productId) {
      return this.resolveProductUnitValue(product);
    }

    if (this.productUnitValueById.has(String(productId))) {
      return this.productUnitValueById.get(String(productId));
    }

    const slimOption = this.findOptionInSlimDataById(productId);

    if (slimOption) {
      const optionUnitValue = this.resolveProductUnitValue(slimOption);
      this.productUnitValueById.set(String(productId), optionUnitValue);
      return optionUnitValue;
    }

    return this.resolveProductUnitValue(product);
  }

  resolveSelectedProductId(product) {
    if (product?.id !== undefined && product?.id !== null) return product.id;
    if (product?.value !== undefined && product?.value !== null)
      return product.value;

    return this.productSelectTarget?.value || null;
  }

  findOptionInSlimDataById(productId) {
    if (!this.productSelect || !this.productSelect.getData) return null;

    const options = this.productSelect.getData() || [];
    const targetId = String(productId);

    return options.find((option) => String(option.value) === targetId) || null;
  }

  saveUnitValueForOption(option) {
    if (!option || option.value === undefined || option.value === null) return;

    const unitValue = this.resolveProductUnitValue(option);
    this.productUnitValueById.set(String(option.value), unitValue);
  }
}
